import _ from "lodash";
import moment from "moment-timezone";
import numeral from "numeral";
import { getCurrencySymbol } from "helpers/Utils";
import { getDateTimeLabel } from "helpers/Chart";
import { APPLIED_CHART_TYPES } from "./AppliedDataHelper";

export const CHART_TYPES = {
  IMPRESSION: "impression",
  PERCENTAGE: "percentage",
};

function getChartConfig(chartType, currency) {
  let yAxisTitle = "";
  let dataFormat = "";
  let prefix = "";
  if (chartType === APPLIED_CHART_TYPES.IMPRESSION) {
    yAxisTitle = "Impressions";
    dataFormat = "0,0";
  } else if (chartType === APPLIED_CHART_TYPES.REVENUE) {
    yAxisTitle = `Revenue (${currency})`;
    dataFormat = "0,0.00";
    prefix = getCurrencySymbol(currency);
  } else if (chartType === APPLIED_CHART_TYPES.ECPM) {
    yAxisTitle = `eCPM (${currency})`;
    dataFormat = "0,0.00";
    prefix = getCurrencySymbol(currency);
  }

  return { yAxisTitle, dataFormat, prefix };
}

export function getPublisherUprPieChartOptions(data) {
  return {
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: "pie",
      height: 200,
    },
    title: {
      text: "",
    },
    tooltip: {
      pointFormat: "<b>{point.percentage:.1f}%</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          distance: -40,
          format: "{point.percentage:.1f}%",
          style: {
            fontSize: "1.2em",
            textOutline: "none",
            opacity: 0.7,
          },
          filter: {
            operator: ">",
            property: "percentage",
            value: 10,
          },
        },
        showInLegend: true,
      },
    },
    series: [
      {
        name: "",
        colorByPoint: true,
        data: _.map(data, (item) => {
          return {
            name: item.name,
            y: item.value,
          };
        }),
      },
    ],
  };
}

export function getPublisherUprImpGeneralChartOptions({
  xAxis,
  impression,
  percentage,
  chartType,
}) {
  return {
    chart: {
      height: 300,
      marginLeft: 72,
    },
    title: {
      text: "",
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        let tooltipText = `<div><div style="text-align: center; font-size: 12px; margin-bottom: 2px;">${this.x}</div>`;
        this.points.forEach(function (point) {
          tooltipText +=
            '<div style="display: flex; width: 100%">' +
            `<span style="color: ${point.color}; padding-right: 4px">\u25CF</span>` +
            '<span style="text-align: left; padding-right: 6px">' +
            point.series.name +
            "</span>" +
            '<span style="text-align: right; margin-left: auto; font-weight: 600">' +
            numeral(point.point.impression).format("0,0") +
            "</span>" +
            '<span style="text-align: right; font-weight: 600; width: 48px">' +
            "(" +
            numeral(point.point.percentage).format("0.0") +
            "%)" +
            "</span>" +
            "</div>";
        });
        tooltipText += "</div>";
        return tooltipText;
      },
    },
    xAxis: {
      categories: xAxis,
      labels: {
        formatter: function () {
          const m = moment(this.value);
          return getDateTimeLabel(m, this.isFirst);
        },
      },
    },
    yAxis: {
      title: {
        text: chartType === CHART_TYPES.IMPRESSION ? "Impression" : "%",
      },
    },
    series: (chartType === CHART_TYPES.IMPRESSION
      ? impression
      : percentage
    ).map((item, idx) => {
      return {
        type: "line",
        name: item.name,
        data: item.data.map((value, dIdx) => {
          return {
            y: value,
            impression: impression[idx].data[dIdx],
            percentage: percentage[idx].data[dIdx],
          };
        }),
        dataLabels: {
          formatter: function () {
            return chartType === CHART_TYPES.IMPRESSION ? this.y : this.y + "%";
          },
        },
      };
    }),
  };
}

export function getAppliedGeneralChartOptions({
  xAxis,
  dataList,
  chartType,
  currency,
  selectFn,
  resetFn,
}) {
  const { yAxisTitle, dataFormat, prefix } = getChartConfig(
    chartType,
    currency
  );

  return {
    chart: {
      height: 280,
      marginLeft: 72,
      zoomType: "x",
      events: {
        selection: function (event) {
          if (event.xAxis) {
            const min = event.xAxis[0].min;
            const max = event.xAxis[0].max;

            selectFn({ min, max });

            return false;
          }
        },
        render: function () {
          const chart = this;

          chart.xAxis[0].ticks &&
            Object.values(chart.xAxis[0].ticks).forEach((tick) => {
              if (tick.label && !tick.label.hasClick) {
                const el = tick.label.element;

                tick.label.originalColor = el.style.fill;

                el.style.cursor = "pointer";

                el.onmouseover = () => {
                  el.style.fill = "#2563eb";
                  el.style.textDecoration = "underline";
                };

                el.onmouseout = () => {
                  el.style.fill = tick.label.originalColor;
                  el.style.textDecoration = "none";
                };

                el.onclick = (e) => {
                  e.stopPropagation();
                  selectFn({ pos: tick.pos });
                };

                tick.label.hasClick = true;
              }
            });
        },
      },
    },
    plotOptions: {
      series: {
        stickyTracking: false,
      },
    },
    title: {
      text: "",
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        let tooltipText = `<div><div style="text-align: center; font-size: 12px; margin-bottom: 2px;">${this.x}</div>`;
        this.points.forEach(function (point) {
          tooltipText +=
            '<div style="display: flex; width: 100%">' +
            `<span style="color: ${point.color}; padding-right: 4px">\u25CF</span>` +
            '<span style="text-align: left; padding-right: 6px">' +
            point.series.name +
            "</span>" +
            '<span style="text-align: right; margin-left: auto; font-weight: 600">' +
            prefix +
            numeral(point.point.y).format(dataFormat) +
            "</span>" +
            "</div>";
        });
        tooltipText += "</div>";
        return tooltipText;
      },
    },
    ...(_.isNil(resetFn)
      ? {}
      : {
          exporting: {
            buttons: {
              contextButton: {
                enabled: false,
              },
              resetButton: {
                text: "Reset",
                align: "right",
                verticalAlign: "top",
                x: -10,
                y: 10,
                theme: {
                  fill: "white",
                  stroke: "#cccccc",
                  r: 4,
                  states: {
                    hover: {
                      fill: "#e5e7eb",
                    },
                  },
                },
                onclick: function () {
                  resetFn();
                },
              },
            },
          },
        }),
    xAxis: {
      crosshair: {
        enabled: true,
        width: 1,
        color: "#cbd5e1",
        dashStyle: "Dash",
        snap: false,
      },
      categories: xAxis,
      labels: {
        formatter: function () {
          const m = moment(this.value);
          return getDateTimeLabel(m, this.isFirst);
        },
      },
    },
    yAxis: {
      crosshair: {
        enabled: true,
        width: 1,
        color: "#cbd5e1",
        dashStyle: "Dash",
        snap: false,
      },
      title: {
        text: yAxisTitle,
      },
    },
    series: dataList.map(({ name, data }) => ({
      type: "line",
      name,
      data,
    })),
  };
}

export function getPublisherFLSubChartOptions({
  xAxis,
  dataList,
  chartType,
  currency,
  title = "",
}) {
  if (dataList.length === 0) {
    return null;
  }
  const LENGTH = 10;
  const { yAxisTitle, dataFormat, prefix } = getChartConfig(
    chartType,
    currency
  );

  const sortedDataList = _.orderBy(dataList, (item) => _.sum(item.data), [
    "desc",
  ]);

  const parsedDataList =
    sortedDataList.length > LENGTH
      ? [
          ...sortedDataList.slice(0, LENGTH - 1),
          {
            name: "Others",
            data: _.zipWith(
              ...sortedDataList.slice(LENGTH - 1).map((d) => d.data),
              (...nums) => _.sum(nums)
            ),
            color: "#a0522d",
          },
        ]
      : sortedDataList;

  return {
    chart: {
      height: 280,
      marginLeft: 72,
    },
    title: {
      text: title,
      align: "left",
      x: 6,
    },
    legend: {
      layout: "vertical",
      align: "right",
      verticalAlign: "middle",
      x: -10,
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        let tooltipText = `<div><div style="text-align: center; font-size: 12px; margin-bottom: 2px;">${this.x}</div>`;
        this.points.forEach(function (point) {
          tooltipText +=
            '<div style="display: flex; width: 100%">' +
            `<span style="color: ${point.color}; padding-right: 4px">\u25CF</span>` +
            '<span style="text-align: left; padding-right: 6px">' +
            point.series.name +
            "</span>" +
            '<span style="text-align: right; margin-left: auto; font-weight: 600">' +
            prefix +
            numeral(point.point.y).format(dataFormat) +
            "</span>" +
            "</div>";
        });
        tooltipText += "</div>";
        return tooltipText;
      },
    },
    xAxis: {
      categories: xAxis,
      labels: {
        formatter: function () {
          const m = moment(this.value);
          return getDateTimeLabel(m, this.isFirst);
        },
      },
    },
    yAxis: {
      title: {
        text: yAxisTitle,
      },
    },
    series: parsedDataList.map(({ name, data, color }) => ({
      type: "line",
      name,
      data,
      color,
    })),
  };
}

export function getAppliedUprSubChartOptions({
  xAxis,
  dataList,
  chartType,
  currency,
  title = "",
}) {
  if (dataList.length === 0) return null;

  const LENGTH = 10;
  const { yAxisTitle, dataFormat, prefix } = getChartConfig(
    chartType,
    currency
  );

  const sortedDataList = _.orderBy(dataList, (item) => _.sum(item.data), [
    "desc",
  ]);

  const parsedDataList =
    sortedDataList.length > LENGTH
      ? [
          ...sortedDataList.slice(0, LENGTH - 1),
          {
            name: "Others",
            data: _.zipWith(
              ...sortedDataList.slice(LENGTH - 1).map((d) => d.data),
              (...nums) => _.sum(nums)
            ),
            ratio: _.zipWith(
              ...sortedDataList.slice(LENGTH - 1).map((d) => d.ratio),
              (...nums) => _.sum(nums)
            ),
            color: "#a0522d",
          },
        ]
      : sortedDataList;

  return {
    chart: {
      height: 280,
      marginLeft: 72,
    },
    title: {
      text: title,
      align: "left",
      x: 6,
    },
    legend: {
      layout: "vertical",
      align: "right",
      verticalAlign: "middle",
      x: -10,
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        let tooltipText = `<div><div style="text-align: center; font-size: 12px; margin-bottom: 2px;">${this.x}</div>`;
        this.points.forEach(function (point) {
          tooltipText +=
            '<div style="display: flex; width: 100%">' +
            `<span style="color: ${point.color}; padding-right: 4px">\u25CF</span>` +
            '<span style="text-align: left; padding-right: 6px; width: 72px">' +
            point.series.name +
            "</span>" +
            (!_.isNil(point.point.ratio)
              ? '<span style="text-align: right; margin-left: 8px; font-weight: 600; width: 48px">' +
                numeral(point.point.ratio).format("0.00%") +
                "</span>" +
                '<span style="text-align: right; margin-left: auto; padding-left: 8px">' +
                prefix +
                numeral(point.point.y).format(dataFormat) +
                "</span>"
              : '<span style="text-align: right; margin-left: auto;">' +
                prefix +
                numeral(point.point.y).format(dataFormat) +
                "</span>") +
            "</div>";
        });
        tooltipText += "</div>";
        return tooltipText;
      },
    },
    xAxis: {
      categories: xAxis,
      labels: {
        formatter: function () {
          const m = moment(this.value);
          return getDateTimeLabel(m, this.isFirst);
        },
      },
    },
    yAxis: {
      title: {
        text: yAxisTitle,
      },
    },
    series: parsedDataList.map(({ name, data, ratio }) => ({
      type: "line",
      name,
      data: data.map((d, idx) => ({ y: d, ratio: ratio[idx] })),
    })),
  };
}
